import {
  backButton,
  init,
  miniApp,
  restoreInitData,
  retrieveLaunchParams,
  swipeBehavior,
  themeParams,
  viewport,
} from '@telegram-apps/sdk-react';
import { env } from '@/shared/config';

/** `ifAvailable()` возвращает [вызвано?, результат] — промис лежит вторым элементом. */
type Attempt = readonly [called: true, data: unknown] | readonly [called: false];

/**
 * Часть методов ждёт ответного события от клиента Telegram. Если клиент считает
 * метод неизвестным, он просто молчит — и промис не резолвится никогда. Без
 * ограничения по времени приложение навсегда осталось бы на нативном лоадере.
 */
const STEP_TIMEOUT_MS = 1500;

/**
 * Совпадает с `--color-bg` (app/styles). Клиент красит этим цветом всё, что
 * лежит за пределами страницы: шапку, нижнюю панель и зазор от резинового
 * отскока. По умолчанию там серая подложка темы, и она заметна на фоне
 * приложения.
 */
const BACKGROUND_COLOR = '#f2f7fd';

/**
 * Полный экран — только в мобильном Telegram. На компьютере (tdesktop, macos,
 * веб-версии) он разворачивал мини-апп на весь монитор; там окно остаётся
 * обычным, как у других мини-аппов (2026-10-05).
 */
const FULLSCREEN_PLATFORMS = new Set(['android', 'android_x', 'ios']);

function isMobilePlatform(): boolean {
  try {
    return FULLSCREEN_PLATFORMS.has(retrieveLaunchParams().tgWebAppPlatform);
  } catch {
    return false;
  }
}

function withTimeout(value: unknown, label: string): Promise<unknown> {
  return Promise.race([
    Promise.resolve(value),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`${label}: клиент не ответил`)), STEP_TIMEOUT_MS),
    ),
  ]);
}

/**
 * Каждый скоуп поднимаем отдельно и не роняем запуск, если что-то не поддержано:
 * версии клиентов Telegram сильно разъезжаются (полный экран и safe area — с 8.0,
 * swipe behavior — с 7.7), а на неподдерживаемом методе SDK бросает.
 *
 * @returns удалось ли выполнить шаг — от этого зависят зависимые шаги.
 */
async function step(label: string, run: () => Attempt): Promise<boolean> {
  // Метки для Performance-панели (chrome://inspect на телефоне): какой шаг
  // реально держит старт, на устройствах ещё не мерили (P1-2).
  const mark = `tg:${label}`;
  performance.mark(mark);
  try {
    const result = run();
    if (!result[0]) {
      console.info(`[telegram] ${label}: недоступно в этом клиенте`);
      return false;
    }
    await withTimeout(result[1], label);
    return true;
  } catch (error) {
    console.warn(`[telegram] ${label}: пропущено`, error);
    return false;
  } finally {
    performance.measure(mark, mark);
  }
}

/**
 * Первая, синхронная часть: после неё уже есть initData — можно слать `/me`
 * и угадывать язык, не дожидаясь, пока клиент Telegram ответит на остальное
 * (`mountTelegramUi`).
 */
export async function initTelegram(): Promise<void> {
  if (env.isDev) {
    const { mockTelegramEnvironment } = await import('./mock-env');
    await mockTelegramEnvironment();
  }

  init();
  restoreInitData();
}

/**
 * Вторая часть: вьюпорт, цвета, жесты. До первого рендера — CSS-переменные
 * вьюпорта нужны первому экрану, иначе контент прыгает под шапкой Telegram.
 *
 * Независимые цепочки идут параллельно: у каждого шага таймаут до 1.5 с, и
 * последовательно на старом клиенте, который молчит на неизвестные методы,
 * они складывались бы в секунды. Последовательно внутри цепочки — только то,
 * что зависит от предыдущего шага.
 */
export async function mountTelegramUi(): Promise<void> {
  await step('themeParams.mount', () => themeParams.mountSync.ifAvailable());

  const viewportChain = async () => {
    // Монтируем до входа в fullscreen: клиент строит прозрачную шапку под
    // полноэкранный режим на основе уже смонтированных компонентов — если
    // backButton смонтирован позже, угловой контрол иногда застревает в виде
    // крестика вместо стрелки назад даже после show().
    await step('backButton.mount', () => backButton.mount.ifAvailable());

    if (await step('viewport.mount', () => viewport.mount.ifAvailable())) {
      // Даёт --tg-viewport-height и --tg-viewport-*-safe-area-inset-*; на них
      // завязаны отступы шелла (см. app/styles/index.css).
      await step('viewport.bindCssVars', () => viewport.bindCssVars.ifAvailable());
      await step('viewport.expand', () => viewport.expand.ifAvailable());
      // Полноэкранный режим (8.0+): контент уходит под системный статус-бар и
      // шапку Telegram — это компенсируют safe-area переменные выше. Только на
      // телефоне, см. `FULLSCREEN_PLATFORMS`.
      if (isMobilePlatform()) {
        await step('viewport.requestFullscreen', () => viewport.requestFullscreen.ifAvailable());
      }
    }
  };

  // Без этого вертикальный свайп по списку опций закрывает мини-апп.
  const swipeChain = async () => {
    if (await step('swipeBehavior.mount', () => swipeBehavior.mount.ifAvailable())) {
      await step('swipeBehavior.disableVertical', () => swipeBehavior.disableVertical.ifAvailable());
    }
  };

  const colorsChain = async () => {
    if (!(await step('miniApp.mount', () => miniApp.mountSync.ifAvailable()))) return;
    await Promise.all([
      step('miniApp.setBackgroundColor', () => miniApp.setBackgroundColor.ifAvailable(BACKGROUND_COLOR)),
      step('miniApp.setBottomBarColor', () => miniApp.setBottomBarColor.ifAvailable(BACKGROUND_COLOR)),
      // Произвольный цвет для шапки поддерживают не все версии клиента; там, где
      // нет, просим ключ темы — он совпадает с фоном страницы (bg_color = #000,
      // см. themeParams в mock-env и настройки бота).
      step('miniApp.setHeaderColor', () =>
        miniApp.setHeaderColor.ifAvailable(miniApp.setHeaderColor.supports.rgb() ? BACKGROUND_COLOR : 'bg_color'),
      ),
    ]);
  };

  await Promise.all([viewportChain(), swipeChain(), colorsChain()]);

  // ready() убирает нативный лоадер Telegram — только после вьюпорта и цветов,
  // иначе пользователь увидит неинициализированный экран.
  await step('miniApp.ready', () => miniApp.ready.ifAvailable());
}
